export interface DependencyNode {
    elementId: string;
    parameterName: string;
    dependsOn: Set<string>; // "elementId:parameterName"
    dependents: Set<string>; // Elements that depend on this
}

export class DependencyGraph {
    private nodes: Map<string, DependencyNode> = new Map();

    /**
     * Add a dependency: source parameter depends on target parameter.
     * Use when a formula in source refers to target.
     */
    addDependency(
        sourceElement: string,
        sourceParam: string,
        targetElement: string,
        targetParam: string
    ): void {
        const sourceKey = this.makeKey(sourceElement, sourceParam);
        const targetKey = this.makeKey(targetElement, targetParam);

        this.ensureNode(sourceKey, sourceElement, sourceParam);
        this.ensureNode(targetKey, targetElement, targetParam);

        this.nodes.get(sourceKey)!.dependsOn.add(targetKey);
        this.nodes.get(targetKey)!.dependents.add(sourceKey);
    }

    /**
     * Remove a specific dependency.
     */
    removeDependency(
        sourceElement: string,
        sourceParam: string,
        targetElement: string,
        targetParam: string
    ): void {
        const sourceKey = this.makeKey(sourceElement, sourceParam);
        const targetKey = this.makeKey(targetElement, targetParam);

        const sourceNode = this.nodes.get(sourceKey);
        const targetNode = this.nodes.get(targetKey);

        if (sourceNode) sourceNode.dependsOn.delete(targetKey);
        if (targetNode) targetNode.dependents.delete(sourceKey);
    }

    /**
     * Get all parameters that need to be recalculated when a parameter changes.
     * Returns them in topological order (target parameters first, then those that depend on them).
     * @param elementId The element ID of the changed parameter
     * @param parameterName The name of the changed parameter
     */
    getAffectedParameters(elementId: string, parameterName: string): string[] {
        const key = this.makeKey(elementId, parameterName);
        const affected: string[] = [];
        const visited = new Set<string>();

        // DFS to find all dependents
        const visit = (nodeKey: string) => {
            if (visited.has(nodeKey)) return;
            visited.add(nodeKey);

            const node = this.nodes.get(nodeKey);
            if (node) {
                // Visit children (dependents) first? 
                // No, for topological sort of *update order*, we want to process dependencies before dependents.
                // But here we are traversing "dependents" (A -> B means B depends on A).
                // If A changes, B needs update. If B has dependent C (C depends on B), C needs update.
                // We must update B, then C.
                // So if we push AFTER visiting children, we get C, then B.
                // Reverse that to get B, then C.

                node.dependents.forEach(depKey => visit(depKey));
                affected.push(nodeKey);
            }
        };

        visit(key);

        // Remove the original parameter (it's the trigger, logic elsewhere handles its update)
        // Reverse because post-order traversal (children first) needs to be flipped for execution order (parent first).
        return affected.filter(k => k !== key).reverse();
    }

    /**
     * Clear all outgoing dependencies for an element's parameter (e.g. before setting a new formula).
     * This removes "this parameter depends on X" links, but keeps "Y depends on this parameter" links.
     */
    clearDependenciesForParameter(elementId: string, parameterName: string): void {
        const sourceKey = this.makeKey(elementId, parameterName);
        const node = this.nodes.get(sourceKey);

        if (node) {
            // For every node currently being depended on, remove this node from its dependents list
            node.dependsOn.forEach(targetKey => {
                const targetNode = this.nodes.get(targetKey);
                if (targetNode) {
                    targetNode.dependents.delete(sourceKey);
                }
            });
            node.dependsOn.clear();
        }
    }

    /**
     * Clear all outgoing dependencies for an entire element (e.g. when element is deleted or fully reset).
     */
    clearElementDependencies(elementId: string): void {
        const toRemove: string[] = [];

        this.nodes.forEach((node, key) => {
            if (key.startsWith(elementId + ':')) {
                // Clear this node's outgoing dependencies
                node.dependsOn.forEach(depKey => {
                    const depNode = this.nodes.get(depKey);
                    if (depNode) {
                        depNode.dependents.delete(key);
                    }
                });
                toRemove.push(key);
            }
        });

        // Also found incoming dependencies? 
        // If an element is deleted, other elements depending on it are now broken.
        // ideally we should throw or handle "Orphaned" dependencies, but for now we just remove the nodes.
        // The dependents will still have the key in their 'dependsOn' set until they are also updated.
        // To be safe, we should clean up incoming refs too.
        this.nodes.forEach((_node, _key) => {
            // For each node in the graph, check if it depends on something we are removing
            // This is O(N*M) where M is avg dependencies. Can be slow.
            // Better: iterate 'toRemove' and for each, look at its 'dependents' and clean them.
        });

        // Correct approach for incoming cleanup:
        toRemove.forEach(key => {
            const node = this.nodes.get(key);
            if (node) {
                node.dependents.forEach(dependentKey => {
                    const dependentNode = this.nodes.get(dependentKey);
                    if (dependentNode) {
                        dependentNode.dependsOn.delete(key);
                    }
                });
            }
            this.nodes.delete(key);
        });
    }

    /**
     * Check if adding a dependency would create a cycle.
     * @returns true if a cycle exists
     */
    hasCircularDependency(elementId: string, parameterName: string): boolean {
        const key = this.makeKey(elementId, parameterName);
        const visited = new Set<string>();
        const stack = new Set<string>();

        const hasCycle = (nodeKey: string): boolean => {
            if (stack.has(nodeKey)) return true;
            if (visited.has(nodeKey)) return false;

            visited.add(nodeKey);
            stack.add(nodeKey);

            const node = this.nodes.get(nodeKey);
            if (node) {
                for (const depKey of node.dependsOn) {
                    if (hasCycle(depKey)) return true;
                }
            }

            stack.delete(nodeKey);
            return false;
        };

        return hasCycle(key);
    }

    /**
     * Get the path of the circular dependency if one exists.
     * Useful for error messages.
     * Returns generic strings "Element:Param".
     */
    getCyclePath(elementId: string, parameterName: string): string[] | null {
        const startKey = this.makeKey(elementId, parameterName);
        const visited = new Set<string>();
        const stack: string[] = [];
        const stackSet = new Set<string>();

        const findCycle = (nodeKey: string): boolean => {
            stack.push(nodeKey);
            stackSet.add(nodeKey);
            visited.add(nodeKey);

            const node = this.nodes.get(nodeKey);
            if (node) {
                for (const depKey of node.dependsOn) {
                    if (stackSet.has(depKey)) {
                        // Cycle found! Add the closing loop node to show the connection back
                        stack.push(depKey);
                        return true;
                    }
                    if (!visited.has(depKey)) {
                        if (findCycle(depKey)) return true;
                    }
                }
            }

            stackSet.delete(nodeKey);
            stack.pop();
            return false;
        };

        if (findCycle(startKey)) {
            return stack;
        }
        return null;
    }

    private makeKey(elementId: string, parameterName: string): string {
        return `${elementId}:${parameterName}`;
    }

    private ensureNode(key: string, elementId: string, parameterName: string) {
        if (!this.nodes.has(key)) {
            this.nodes.set(key, {
                elementId,
                parameterName,
                dependsOn: new Set(),
                dependents: new Set()
            });
        }
    }
}
